import React from 'react';
import { Clock, AlertTriangle } from 'lucide-react';
import { useSystemSettings } from '@/hooks/useSystemSettings';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';

import { useNavigate } from 'react-router-dom';
import { useEffect } from 'react';

interface MaintenancePageProps {
    forceActive?: boolean;
}

const MaintenancePage: React.FC<MaintenancePageProps> = ({ forceActive }) => {
    const { settings, loading } = useSystemSettings();
    const navigate = useNavigate();

    useEffect(() => {
        if (!forceActive && !loading && settings && !settings.maintenance_mode) {
            // Se manutenção foi desativada, recarregar a página para o ProtectedRoute liberar o acesso
            window.location.reload();
        }
    }, [settings?.maintenance_mode, loading, navigate, forceActive]);

    const formattedReturnTime = settings?.maintenance_return_time
        ? format(new Date(settings.maintenance_return_time), "dd 'de' MMMM 'às' HH:mm", { locale: ptBR })
        : null;

    return (
        <div className="min-h-screen w-full flex items-center justify-center bg-[#004D2C] text-white p-4 overflow-hidden relative selection:bg-green-500/30">
            {/* Background Effect - Green Tones */}
            <div className="absolute inset-0 overflow-hidden pointer-events-none">
                <div className="absolute top-[-20%] left-[-10%] w-[60%] h-[60%] bg-[#006039]/40 rounded-full blur-[120px]" />
                <div className="absolute bottom-[-10%] right-[-5%] w-[50%] h-[50%] bg-[#003820]/60 rounded-full blur-[100px]" />
            </div>

            <div className="relative z-10 max-w-lg w-full bg-white/5 backdrop-blur-md border border-white/10 rounded-3xl p-8 md:p-12 shadow-2xl text-center">
                {/* Watch Logo */}
                <div className="relative mx-auto mb-10 flex items-center justify-center">
                    <img
                        src="https://link.letswatch.com.br/storage/27001/new-watch-Smart-Media-logo-white.webp"
                        alt="Watch Smart Media"
                        className="h-20 md:h-24 w-auto object-contain drop-shadow-lg"
                    />
                </div>

                <h1 className="text-3xl md:text-4xl font-bold mb-4 text-white">
                    Manutenção Programada
                </h1>

                <p className="text-gray-300 text-lg mb-10 leading-relaxed font-light">
                    Estamos realizando atualizações importantes para melhorar sua experiência.
                </p>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-left mb-8">
                    <div className="bg-[#003820]/40 rounded-xl p-4 border border-white/5 hover:bg-[#003820]/60 transition-colors">
                        <div className="flex items-center gap-3 mb-2">
                            <Clock className="w-5 h-5 text-green-400" />
                            <h3 className="font-semibold text-green-50">Previsão de Retorno</h3>
                        </div>
                        <p className="text-sm text-gray-300 font-medium whitespace-pre-line">
                            {formattedReturnTime ? formattedReturnTime : "Voltaremos em\nalguns instantes."}
                        </p>
                    </div>

                    <div className="bg-[#003820]/40 rounded-xl p-4 border border-white/5 hover:bg-[#003820]/60 transition-colors">
                        <div className="flex items-center gap-3 mb-2">
                            <AlertTriangle className="w-5 h-5 text-yellow-400" />
                            <h3 className="font-semibold text-green-50">Status</h3>
                        </div>
                        <p className="text-sm text-gray-300 font-medium">Atualização em andamento.</p>
                    </div>
                </div>

                <div className="mt-8 text-xs text-gray-400 border-t border-white/10 pt-6">
                    © 2026 Watch Technologies | Smart Media. Todos os direitos reservados.
                </div>
            </div>
        </div>
    );
};

export default MaintenancePage;
